import { CURRENCY } from "store/apis/balanceAPI/values"
import { bossArenaCycleAPI } from "store/apis/bossArenaCycleAPI"
import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { bossClansAPI } from "store/apis/bossLeaderboardAPI/bossLeaderboard"
import {
  IBossStats,
  IBossUpgradeCost,
  IBossEnergy,
  IBossEnergyUpgradeCost,
  IBossAttackResult,
  BossStatType,
  IBossHealth,
  IBossEnterResult,
  IBossCollectDropResult,
  IBossCollectDropRequest,
  IBossUpgradeResult,
  IBossEnergyUpgradeResult,
  ICombinedLogs,
  IBossAbilities,
  IBossActivateBerserkerResult,
  IBossDarkRespecInfo,
  IBossDarkRespecResult,
  IPvpAttackRequest,
  IPvpAttackResult,
  IFindOpponentResult,
  IArenaAttackRequest,
  IArenaAttackResult,
  DARK_RESPEC_POINTS_CURRENCY
} from "store/apis/bossAPI/boss.types"
import { IPaginationReq } from "utils/interfaces.ts"
import { api, TAGS } from "../../api.ts"

export const bossApi = api.enhanceEndpoints({
  addTagTypes: [
    "BossEnergy",
    "BossEnergyUpgradeCost",
    TAGS.BOSS,
    TAGS.MISSIONS,
    TAGS.BOSS_LEADERBOARD
  ]
})

export const bossEndpoints = bossApi.injectEndpoints({
  endpoints: (builder) => ({
    getBossStats: builder.query<IBossStats, void>({
      query: () => ({
        url: "/boss/stats",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.BOSS, id: "STATS" }]
    }),
    getBossUpgradeCost: builder.query<IBossUpgradeCost, BossStatType>({
      query: (type) => ({
        url: `/boss/upgrade-cost/${type}`,
        method: "GET"
      }),
      providesTags: (_, __, type) => [
        { type: TAGS.BOSS, id: `UPGRADE_COST:${type}` },
        { type: TAGS.BOSS, id: "UPGRADE_COSTS" }
      ]
    }),
    upgradeBossStat: builder.mutation<IBossUpgradeResult, BossStatType>({
      query: (type) => ({
        url: `/boss/upgrade/${type}`,
        method: "POST"
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const prevStats = bossEndpoints.endpoints.getBossStats.select()(
          getState() as never
        ).data
        const prevRestored = prevStats?.darkRespecUpgradesRemaining ?? 0

        const prevUpgradeCost =
          bossEndpoints.endpoints.getBossUpgradeCost.select(arg)(
            getState() as never
          ).data
        const prevCostCurrency = prevUpgradeCost?.cost?.currency

        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossStats",
            undefined,
            () => data.newStats
          )
        )
        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossUpgradeCost",
            arg,
            () => data.nextUpgradeCost
          )
        )

        const nextRestored = data.newStats.darkRespecUpgradesRemaining ?? 0
        const nextCostCurrency = data.nextUpgradeCost?.cost?.currency

        const restoredJustDepleted = prevRestored > 0 && nextRestored === 0
        const pointsJustDepleted =
          prevCostCurrency === DARK_RESPEC_POINTS_CURRENCY &&
          nextCostCurrency !== DARK_RESPEC_POINTS_CURRENCY

        const shouldRefreshUpgradeCosts =
          restoredJustDepleted || pointsJustDepleted

        dispatch(
          bossEndpoints.util.invalidateTags([
            { type: TAGS.BOSS, id: "DARK_RESPEC" },
            ...(shouldRefreshUpgradeCosts
              ? ([{ type: TAGS.BOSS, id: "UPGRADE_COSTS" }] as const)
              : ([] as const)),
            ...(arg === "energyRegen"
              ? (["BossEnergy"] as const)
              : ([] as const))
          ])
        )
      }
    }),
    getBossUpgrades: builder.query<IBossStats, string>({
      query: (userId) => ({
        url: `/boss/stats/${userId}`,
        method: "GET"
      }),
      providesTags: (_, __, userId) => [
        {
          type: TAGS.BOSS,
          id: `UPGRADES:${userId}`
        }
      ]
    }),
    getBossEnergy: builder.query<IBossEnergy, void>({
      query: () => ({
        url: "/boss/energy",
        method: "GET"
      }),
      providesTags: ["BossEnergy"]
    }),
    getBossEnergyUpgradeCost: builder.query<IBossEnergyUpgradeCost, void>({
      query: () => ({
        url: "/boss/energy/upgrade-cost",
        method: "GET",
        params: { currency: CURRENCY.TON }
      }),
      providesTags: ["BossEnergyUpgradeCost"],
      keepUnusedDataFor: 0
    }),
    upgradeBossEnergy: builder.mutation<IBossEnergyUpgradeResult, void>({
      query: () => ({
        url: "/boss/energy/upgrade",
        method: "POST",
        data: { paymentMethod: CURRENCY.TON }
      }),
      invalidatesTags: ["BossEnergyUpgradeCost", "BossEnergy"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        if (data.newBalance) {
          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance!
            )
          )
        }

        if (data.newMax) {
          dispatch(
            bossEndpoints.util.updateQueryData(
              "getBossEnergy",
              undefined,
              (draft) => {
                draft.pve.max = data.newMax!
                draft.pvp.max = data.newMax!
                if (data.energyFilled) {
                  draft.pve.current = data.newMax!
                  draft.pvp.current = data.newMax!
                }
              }
            )
          )
        }
      }
    }),
    recordFailedEnergyUpgrade: builder.mutation<
      { success: boolean },
      { status: "failed" | "cancelled" }
    >({
      query: (data) => ({
        url: "/boss/energy/upgrade-failed",
        method: "POST",
        data
      })
    }),
    getBossHealth: builder.query<IBossHealth, void>({
      query: () => ({
        url: "/boss/health",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.BOSS, id: "HEALTH" }]
    }),
    attackBoss: builder.mutation<IBossAttackResult, { isHit: boolean }>({
      query: (data) => ({
        url: "/boss/attack",
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossEnergy",
            undefined,
            (draft) => {
              draft.pve = data.energy
            }
          )
        )

        dispatch(
          bossEndpoints.util.invalidateTags([
            { type: TAGS.BOSS, id: "ACTIVITY_LOGS" },
            { type: TAGS.BOSS, id: "STATS" },
            { type: TAGS.BOSS, id: "HEALTH" }
          ])
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )

        dispatch(
          bossClansAPI.util.invalidateTags([
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_PERSONAL" },
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_CLANS" }
          ])
        )
      }
    }),
    getBossPendingDrops: builder.query<IBossEnterResult, void>({
      query: () => ({
        url: "/boss/pending-drops",
        method: "GET"
      }),
      keepUnusedDataFor: 0
    }),
    getBossAbilities: builder.query<IBossAbilities, void>({
      query: () => ({
        url: "/boss/abilities",
        method: "GET"
      })
    }),
    getDarkRespecInfo: builder.query<IBossDarkRespecInfo, void>({
      query: () => ({
        url: "/boss/dark-respec-info",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.BOSS, id: "DARK_RESPEC" }]
    }),
    darkRespec: builder.mutation<IBossDarkRespecResult, void>({
      query: () => ({
        url: "/boss/dark-respec",
        method: "POST"
      }),
      invalidatesTags: [
        { type: TAGS.BOSS, id: "DARK_RESPEC" },
        { type: TAGS.BOSS, id: "UPGRADE_COSTS" },
        "BossEnergy",
        "BossEnergyUpgradeCost"
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        if (data.newBalance) {
          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance!
            )
          )
        }

        if (data.newStats) {
          dispatch(
            bossEndpoints.util.updateQueryData(
              "getBossStats",
              undefined,
              () => data.newStats!
            )
          )
        }
      }
    }),
    activateBerserker: builder.mutation<IBossActivateBerserkerResult, void>({
      query: () => ({
        url: "/boss/berserker/activate",
        method: "POST"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossAbilities",
            undefined,
            (draft) => {
              draft.berserker.isActive = true
              draft.berserker.endAt = data.berserkerEndAt
            }
          )
        )

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            (draft) => {
              draft.gold = (draft.gold ?? 0) - data.goldSpent
            }
          )
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )
      }
    }),
    collectDrop: builder.mutation<
      IBossCollectDropResult,
      IBossCollectDropRequest
    >({
      query: (data) => ({
        url: "/boss/collect-drop",
        method: "POST",
        data
      }),
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const patchBalance = dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            (draft) => {
              draft[arg.currency] = (draft[arg.currency] ?? 0) + arg.amount
            }
          )
        )

        try {
          await queryFulfilled
        } catch {
          patchBalance.undo()
        }
      }
    }),
    getBossActivityLogs: builder.query<ICombinedLogs, IPaginationReq>({
      query: (params) => ({
        url: "/boss/activity-logs",
        method: "GET",
        params: params
      }),
      providesTags: (_, __, { page, limit }) => [
        { type: TAGS.BOSS, id: "ACTIVITY_LOGS" },
        { type: TAGS.BOSS, id: `ACTIVITY_LOGS:${page}:${limit}` }
      ]
    }),
    getPvpLogs: builder.query<ICombinedLogs, IPaginationReq>({
      query: (params) => ({
        url: "/boss/pvp-logs",
        method: "GET",
        params: params
      }),
      providesTags: (_, __, { page, limit }) => [
        { type: TAGS.BOSS, id: "PVP_LOGS" },
        { type: TAGS.BOSS, id: `PVP_LOGS:${page}:${limit}` }
      ]
    }),
    findArenaOpponent: builder.mutation<IFindOpponentResult, void>({
      query: () => ({
        url: "/boss/arena/find-opponent",
        method: "POST"
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled

        dispatch(
          bossEndpoints.util.invalidateTags([
            { type: TAGS.BOSS, id: "ARENA_MATCH" },
            "BossEnergy"
          ])
        )
      }
    }),
    getArenaCurrentMatch: builder.query<IFindOpponentResult, void>({
      query: () => ({
        url: "/boss/arena/current-match",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.BOSS, id: "ARENA_MATCH" }]
    }),
    arenaAttack: builder.mutation<IArenaAttackResult, IArenaAttackRequest>({
      query: (data) => ({
        url: "/boss/arena/attack",
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossEnergy",
            undefined,
            (draft) => {
              draft.pvp.current = data.attackerEnergy
            }
          )
        )

        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossStats",
            undefined,
            (draft) => {
              draft.totalDamage = data.attackerNewTotalDamage
              draft.position = data.attackerNewPosition
            }
          )
        )

        dispatch(
          bossEndpoints.util.invalidateTags([
            { type: TAGS.BOSS, id: "PVP_LOGS" },
            { type: TAGS.BOSS, id: "ARENA_MATCH" },
            { type: TAGS.BOSS, id: "STATS" },
            "BossEnergy"
          ])
        )

        dispatch(
          bossClansAPI.util.invalidateTags([
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_PERSONAL" },
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_CLANS" }
          ])
        )

        dispatch(
          bossArenaCycleAPI.util.invalidateTags([
            { type: TAGS.BOSS_ARENA_CYCLE_LB, id: "LIST" },
            { type: TAGS.BOSS_ARENA_CYCLE_STATE, id: "CURRENT" }
          ])
        )
      }
    }),
    pvpAttack: builder.mutation<IPvpAttackResult, IPvpAttackRequest>({
      query: (data) => ({
        url: "/boss/pvp/attack",
        method: "POST",
        data
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled

        dispatch(
          bossEndpoints.util.updateQueryData(
            "getBossEnergy",
            undefined,
            (draft) => {
              draft.pvp.current = data.attackerEnergy
            }
          )
        )

        dispatch(
          bossEndpoints.util.invalidateTags([
            { type: TAGS.BOSS, id: "PVP_LOGS" },
            "BossEnergy"
          ])
        )

        dispatch(
          bossClansAPI.util.invalidateTags([
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_PERSONAL" },
            { type: TAGS.BOSS_LEADERBOARD, id: "LEADERBOARD_CLANS" }
          ])
        )
      }
    })
  })
})
